/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, createContext, PropsWithChildren } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  ButtonGroup,
  Button,
  LoadingButton
} from '@one-thd/sui-atomic-components';
import { IButtonProps } from '../models/common-models';

const initialState = {
  ContentComponent: null,
  componentProps: null,
  title: '',
  isFooterFlex: false,
  isOpened: false,
  cancelProps: {
    buttonText: 'Cancel',
    onClick: () => {}
  },
  saveChangesProps: {
    buttonText: 'Save',
    onClick: () => {}
  },
  hideFooter: false
};

interface IDrawerState {
  title?: string;
  isOpened?: boolean;
  isFooterFlex?: boolean;
  ContentComponent: React.FC<PropsWithChildren<any>> | null;
  componentProps?: PropsWithChildren<any>;
  saveChangesProps?: IButtonProps;
  cancelProps?: IButtonProps;
  hideFooter?: boolean;
}

interface IDrawerProviderProps {
  children: React.ReactNode;
}

interface IDrawerContextValues {
  isOpened: boolean;
  openDrawer: (_drawerState: IDrawerState) => void;
  closeDrawer: () => void;
  updateDrawerState: (_drawerState: IDrawerState) => void;
}

export const DrawerContext = createContext<IDrawerContextValues | null>(null);

export const DrawerProvider = ({ children }: IDrawerProviderProps) => {
  const [drawerState, setDrawerState] = useState<IDrawerState>(initialState);

  const openDrawer = (_drawerState: IDrawerState) => {
    setDrawerState((prevState) => ({
      ...prevState,
      ..._drawerState,
      isOpened: true
    }));
  };

  const closeDrawer = () => {
    if (drawerState.cancelProps && drawerState.cancelProps?.onClick) {
      drawerState.cancelProps?.onClick();
    }
    setDrawerState((prevState) => ({
      ...prevState,
      isOpened: false,
      hideFooter: false
    }));
  };

  const updateDrawerState = (_drawerState: IDrawerState) => {
    setDrawerState((prevDrawerState) => ({
      ...prevDrawerState,
      ..._drawerState,
      saveChangesProps: { ...prevDrawerState.saveChangesProps, ..._drawerState?.saveChangesProps },
      componentProps: { ...prevDrawerState.componentProps, ..._drawerState?.componentProps }
    }));
  };

  return (
    <DrawerContext.Provider
      value={{
        openDrawer,
        closeDrawer,
        isOpened: drawerState.isOpened || false,
        updateDrawerState
      }}
    >
      <Drawer open={drawerState?.isOpened} onClose={closeDrawer} position="right">
        <DrawerHeader title={drawerState?.title} onClose={closeDrawer} />
        <DrawerBody>
          {drawerState.ContentComponent && (
            <drawerState.ContentComponent {...drawerState.componentProps} />
          )}
        </DrawerBody>
        {!drawerState?.hideFooter && (
          <DrawerFooter variant={drawerState?.isFooterFlex ? 'flex' : 'sticky'}>
            <ButtonGroup orientation="vertical">
              <LoadingButton
                variant="primary"
                data-testid="save-drawer-button"
                disabled={!drawerState.saveChangesProps?.onClick}
                {...drawerState.saveChangesProps}
              >
                {drawerState.saveChangesProps?.buttonText || 'Save Changes'}
              </LoadingButton>
              <Button
                data-testid="cancel-drawer-button"
                variant="secondary"
                onClick={closeDrawer}
                {...drawerState.cancelProps}
              >
                {drawerState.cancelProps?.buttonText || 'Cancel'}
              </Button>
            </ButtonGroup>
          </DrawerFooter>
        )}
      </Drawer>
      {children}
    </DrawerContext.Provider>
  );
};
